import z from "zod";
import type { ReviewBidGuideOutcome } from "@remi/enums/review-bid-guide-outcome";
import type { TaskType } from "@remi/enums/task-type";

import { ErrorClassification } from "../../../sentry/classification";
import type { ErrorDetails } from "../domain";

const quotingErrorTypeValues = [
  "UNKNOWN",
  "REQUEST_FOR_QUOTE_NOT_FOUND",
  "BID_NOT_FOUND",
  "NO_VALID_RFQ_FOR_SOW",
  "PROJECT_NOT_FOUND",
  "PDF_NOT_CREATED",
  "REMI_ENTITY_NOT_FOUND",
  "R2_QUOTE_CREATION_FAILED",
  "SUBQUOTES_BID_REQUESTS_NOT_FOUND",
  "NO_ACTIVE_QUOTE",
  "GEOCODING_NOT_FOUND",
  "QUOTE_NOT_FOUND",
  "QUOTE_VERSION_NOT_FOUND",
  "QUOTE_ACTIVATION_SIDE_EFFECT_FAILED",
  "EXTERNAL_QUOTES_NOT_FOUND",
  "MISSING_BID_REQUESTS",
  "QUOTE_VERSION_EXTERNAL_ID_NOT_FOUND",
  "LIDAR_SERVER_ERROR",
  "LIDAR_SERVER_TIMEOUT",
  "TASK_NOT_FOUND",
  "ESTIMATE_NOT_FOUND",
  "SUB_QUOTE_NOT_FOUND",
  "SUB_QUOTE_MISSING_QUOTE_REFERENCE",
  "INVALID_PRICING_TYPE",
  "QUOTE_VERSION_HAS_ERRORS",
  "CONCESSION_LINE_ITEM_ON_QUOTE",
  "PROPERTY_ADDRESS_NOT_FOUND_OR_UNAUTHORIZED",
  "STRATEGIC_PARTNER_MONTHLY_PAYMENT_UNAVAILABLE",
  "SANDBOX_QUOTE_NOT_FOUND",
  "SANDBOX_FILE_NOT_OWNED",
  "SANDBOX_IMAGE_FORMAT_UNSUPPORTED",
  "JAPAN_IMAGERY_FILE_NOT_FOUND",
  "JAPAN_IMAGERY_INVALID_IMAGE",
  "PLATEAU_SOURCE_ERROR",
  "PLATEAU_TIMEOUT",
  "PLATEAU_PARSE_ERROR",
  "CANNOT_EDIT_ARCHIVED_QUOTE_VERSION",
  "PRICE_GUARANTEE_NOT_ALLOWED_FOR_QUOTE",
  "HAND_EDITS_NOT_REPRESENTABLE",
  "INVALID_BASE_QUOTE_VERSION_ANCHOR",
  "QUOTE_VERSION_PINNED_BY_SEND_AGREEMENT",
  "QUOTE_VERSION_OVERRIDE_NOT_ALLOWED",
  "QUOTE_VERSION_OVERRIDES_EMPTY",
  "SALES_DISCOUNT_EXCEEDS_CAP",
  "QUOTE_VERSION_STAGED_FOR_ACTIVATION",
  "REQUEST_FOR_QUOTE_HAS_STAGED_QUOTE",
  "BID_MERGED_INTO_ANOTHER_BID",
  "QUOTE_VERSION_NOT_COPYABLE_TO_REQUEST_FOR_QUOTE",
  "QUOTE_BLOCKED_BY_REVIEW_BID_GUIDE",
] as const;
const quotingErrorTypeSchema = z.enum(quotingErrorTypeValues);
const QuotingErrorType = quotingErrorTypeSchema.enum;
type QuotingErrorType = z.infer<typeof quotingErrorTypeSchema>;

export type QuotingErrors =
  | { errorType: typeof QuotingErrorType.UNKNOWN; data: undefined }
  | { errorType: typeof QuotingErrorType.REQUEST_FOR_QUOTE_NOT_FOUND; data: { requestForQuoteId: number } }
  | { errorType: typeof QuotingErrorType.BID_NOT_FOUND; data: { bidId: number } }
  | { errorType: typeof QuotingErrorType.NO_VALID_RFQ_FOR_SOW; data: { bidId: number } }
  | { errorType: typeof QuotingErrorType.PROJECT_NOT_FOUND; data: { projectId?: number; requestForQuoteId?: number } }
  | {
      errorType: typeof QuotingErrorType.PDF_NOT_CREATED;
      data: { quoteVersionId?: string; subQuoteId?: string };
    }
  | { errorType: typeof QuotingErrorType.REMI_ENTITY_NOT_FOUND; data: { quoteVersionId: string } }
  | { errorType: typeof QuotingErrorType.R2_QUOTE_CREATION_FAILED; data: { errors: string[] } }
  | { errorType: typeof QuotingErrorType.SUBQUOTES_BID_REQUESTS_NOT_FOUND; data: { requestForQuoteId: number } }
  | { errorType: typeof QuotingErrorType.NO_ACTIVE_QUOTE; data: { quoteVersionId: string } }
  | { errorType: typeof QuotingErrorType.GEOCODING_NOT_FOUND; data: { addressId: string } }
  | { errorType: typeof QuotingErrorType.QUOTE_NOT_FOUND; data: { quoteVersionId?: string } }
  | { errorType: typeof QuotingErrorType.QUOTE_VERSION_NOT_FOUND; data: { quoteVersionId: string } }
  | {
      errorType: typeof QuotingErrorType.QUOTE_ACTIVATION_SIDE_EFFECT_FAILED;
      data: {
        step: "shingle-line-change" | "activity-log" | "hubspot-upsert";
        requestForQuoteId: number;
        activatedQuoteVersionId: string;
        bidId: number;
        reason: string;
      };
    }
  | { errorType: typeof QuotingErrorType.EXTERNAL_QUOTES_NOT_FOUND; data: { rfqId: number } }
  | { errorType: typeof QuotingErrorType.MISSING_BID_REQUESTS; data: { rfqId: number } }
  | { errorType: typeof QuotingErrorType.QUOTE_VERSION_EXTERNAL_ID_NOT_FOUND; data: { quoteVersionId: string } }
  | {
      errorType: typeof QuotingErrorType.BID_MERGED_INTO_ANOTHER_BID;
      data: { requestForQuoteId: number; bidId: number; mergedIntoBidId: number | null };
    }
  | {
      errorType: typeof QuotingErrorType.QUOTE_BLOCKED_BY_REVIEW_BID_GUIDE;
      data: { requestForQuoteId: number; bidId: number; outcome: ReviewBidGuideOutcome; guideVersion: number };
    }
  | { errorType: typeof QuotingErrorType.LIDAR_SERVER_ERROR; data: { addressId: string; errorMessage: string } }
  | { errorType: typeof QuotingErrorType.LIDAR_SERVER_TIMEOUT; data: { addressId: string; timeoutMs: number } }
  | { errorType: typeof QuotingErrorType.TASK_NOT_FOUND; data: { taskType?: TaskType; projectId?: number } }
  | { errorType: typeof QuotingErrorType.ESTIMATE_NOT_FOUND; data: { estimateId: string } }
  | { errorType: typeof QuotingErrorType.SUB_QUOTE_NOT_FOUND; data: { subQuoteId?: string } }
  | { errorType: typeof QuotingErrorType.SUB_QUOTE_MISSING_QUOTE_REFERENCE; data: { subQuoteId?: string } }
  | {
      errorType: typeof QuotingErrorType.INVALID_PRICING_TYPE;
      data: { lineItemTypeName: string; pricingType: string; quoteVersionId: string };
    }
  | { errorType: typeof QuotingErrorType.QUOTE_VERSION_HAS_ERRORS; data: { errors: string[] } }
  | {
      errorType: typeof QuotingErrorType.CONCESSION_LINE_ITEM_ON_QUOTE;
      data: { requestForQuoteId: number; concessionLineItemTypeNames: string[] };
    }
  | {
      errorType: typeof QuotingErrorType.PROPERTY_ADDRESS_NOT_FOUND_OR_UNAUTHORIZED;
      data: { propertyAddressId: string };
    }
  | {
      errorType: typeof QuotingErrorType.STRATEGIC_PARTNER_MONTHLY_PAYMENT_UNAVAILABLE;
      data: {
        quoteVersionId: string;
        requestForQuoteId: number;
        financingId: string | null;
        financingStatus: string | null;
      };
    }
  | {
      errorType: typeof QuotingErrorType.SANDBOX_QUOTE_NOT_FOUND;
      data: { sandboxQuoteId: string; organizationId: string };
    }
  | {
      errorType: typeof QuotingErrorType.SANDBOX_FILE_NOT_OWNED;
      data: { fileId: string; organizationId: string };
    }
  | {
      errorType: typeof QuotingErrorType.SANDBOX_IMAGE_FORMAT_UNSUPPORTED;
      data: { fileId: string; name: string };
    }
  | {
      errorType: typeof QuotingErrorType.JAPAN_IMAGERY_FILE_NOT_FOUND;
      data: { tifFileId: string; tfwFileId: string };
    }
  | { errorType: typeof QuotingErrorType.JAPAN_IMAGERY_INVALID_IMAGE; data: { tifFileId: string } }
  | { errorType: typeof QuotingErrorType.PLATEAU_SOURCE_ERROR; data: { coordKey: string; status?: number } }
  | { errorType: typeof QuotingErrorType.PLATEAU_TIMEOUT; data: { coordKey: string } }
  | { errorType: typeof QuotingErrorType.PLATEAU_PARSE_ERROR; data: { coordKey: string } }
  | { errorType: typeof QuotingErrorType.CANNOT_EDIT_ARCHIVED_QUOTE_VERSION; data: { quoteVersionId: string } }
  | {
      errorType: typeof QuotingErrorType.PRICE_GUARANTEE_NOT_ALLOWED_FOR_QUOTE;
      data: { quoteVersionId: string; requested: boolean };
    }
  | {
      errorType: typeof QuotingErrorType.HAND_EDITS_NOT_REPRESENTABLE;
      data: {
        requestForQuoteId: number;
        unrepresentedEdits: {
          lineItemTypeName: string;
          customName: string | null;
          pricingType: string;
          quantity: number;
          unitPrice: number;
          customProjectCategory: string | null;
        }[];
      };
    }
  | { errorType: typeof QuotingErrorType.INVALID_BASE_QUOTE_VERSION_ANCHOR; data: { quoteVersionId: string } }
  | {
      errorType: typeof QuotingErrorType.QUOTE_VERSION_NOT_COPYABLE_TO_REQUEST_FOR_QUOTE;
      data: { sourceQuoteVersionId: string; targetRequestForQuoteId: number; partnerFlow: string };
    }
  | {
      errorType: typeof QuotingErrorType.QUOTE_VERSION_PINNED_BY_SEND_AGREEMENT;
      data: { quoteVersionId: string; projectId: number };
    }
  | {
      errorType: typeof QuotingErrorType.QUOTE_VERSION_OVERRIDE_NOT_ALLOWED;
      data: { quoteVersionId: string; appliedChanges: string[] };
    }
  | { errorType: typeof QuotingErrorType.QUOTE_VERSION_OVERRIDES_EMPTY; data: { quoteVersionId: string } }
  | {
      errorType: typeof QuotingErrorType.SALES_DISCOUNT_EXCEEDS_CAP;
      data: { salesDiscountAmount: number; preDiscountTotal: number; requestForQuoteId: number };
    }
  | { errorType: typeof QuotingErrorType.QUOTE_VERSION_STAGED_FOR_ACTIVATION; data: undefined }
  | { errorType: typeof QuotingErrorType.REQUEST_FOR_QUOTE_HAS_STAGED_QUOTE; data: undefined };

export const quotingErrorDetails: Record<QuotingErrorType, ErrorDetails> = {
  UNKNOWN: { classification: ErrorClassification.UNEXPECTED_ERROR, sentryDescription: "Unknown Error" },
  REQUEST_FOR_QUOTE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Request for quote not found",
  },
  BID_NOT_FOUND: { classification: ErrorClassification.NOT_FOUND_ERROR, sentryDescription: "Bid not found" },
  NO_VALID_RFQ_FOR_SOW: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No valid RFQ for SOW confirmation",
  },
  PROJECT_NOT_FOUND: { classification: ErrorClassification.NOT_FOUND_ERROR, sentryDescription: "Project not found" },
  PDF_NOT_CREATED: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "PDF was not created successfully",
  },
  REMI_ENTITY_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Remi entity not found for quote version",
  },
  R2_QUOTE_CREATION_FAILED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "R2 quote creation failed",
  },
  SUBQUOTES_BID_REQUESTS_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Required bid requests for subquotes not found",
  },
  NO_ACTIVE_QUOTE: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No active quote found",
  },
  GEOCODING_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Geocoding not found",
  },
  QUOTE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Quote not found",
  },
  QUOTE_VERSION_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Quote version not found",
  },
  QUOTE_ACTIVATION_SIDE_EFFECT_FAILED: {
    classification: ErrorClassification.DEPENDENCY_ERROR,
    sentryDescription: "Quote version was activated but a post-commit side effect failed",
  },
  EXTERNAL_QUOTES_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "External quotes not found",
  },
  MISSING_BID_REQUESTS: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Missing bid requests",
  },
  QUOTE_VERSION_EXTERNAL_ID_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Quote version external ID not found",
  },
  LIDAR_SERVER_ERROR: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "LIDAR server error",
  },
  LIDAR_SERVER_TIMEOUT: {
    classification: ErrorClassification.TIMEOUT_ERROR,
    sentryDescription: "LIDAR server request timed out",
  },
  TASK_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Task not found",
  },
  ESTIMATE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Estimate not found",
  },
  SUB_QUOTE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Sub quote not found",
  },
  SUB_QUOTE_MISSING_QUOTE_REFERENCE: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Sub quote has neither bidQuoteId nor quoteVersionId",
  },
  INVALID_PRICING_TYPE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Invalid pricing type for line item",
  },
  QUOTE_VERSION_HAS_ERRORS: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Quote version has validation errors",
  },
  CONCESSION_LINE_ITEM_ON_QUOTE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Quote version carried a work order concession line item",
  },
  PROPERTY_ADDRESS_NOT_FOUND_OR_UNAUTHORIZED: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Property address not found or not accessible to user",
  },
  STRATEGIC_PARTNER_MONTHLY_PAYMENT_UNAVAILABLE: {
    classification: ErrorClassification.DEPENDENCY_ERROR,
    sentryDescription:
      "Strategic Partner quote submission blocked: monthly-payment estimate is missing and the synchronous retry did not produce one — see financingStatus in data",
  },
  SANDBOX_QUOTE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Sandbox quote not found",
  },
  SANDBOX_FILE_NOT_OWNED: {
    classification: ErrorClassification.AUTHORIZATION_ERROR,
    sentryDescription: "Sandbox quote file (image or LiDAR capture) not found or not owned by the caller's org",
  },
  SANDBOX_IMAGE_FORMAT_UNSUPPORTED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Sandbox report image is not a PNG/JPEG that react-pdf can render",
  },
  JAPAN_IMAGERY_FILE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Japan imagery .tif/.tfw file not found in S3",
  },
  JAPAN_IMAGERY_INVALID_IMAGE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Japan imagery GeoTIFF could not be decoded",
  },
  PLATEAU_SOURCE_ERROR: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "PLATEAU building-tile source returned an error",
  },
  PLATEAU_TIMEOUT: {
    classification: ErrorClassification.TIMEOUT_ERROR,
    sentryDescription: "PLATEAU building-tile request timed out",
  },
  PLATEAU_PARSE_ERROR: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "PLATEAU building-tile response could not be decoded",
  },
  CANNOT_EDIT_ARCHIVED_QUOTE_VERSION: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Cannot change price guarantee on an archived quote version",
  },
  PRICE_GUARANTEE_NOT_ALLOWED_FOR_QUOTE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Requested price guarantee value is not allowed for this quote's partner flow or work type",
  },
  HAND_EDITS_NOT_REPRESENTABLE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Hand-edited quote rows cannot be expressed as engine inputs, so saving would drop them",
  },
  INVALID_BASE_QUOTE_VERSION_ANCHOR: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription:
      "Base quote version anchor is invalid (archived, deleted, cross-RFQ, or has elective work / null estimate)",
  },
  QUOTE_VERSION_PINNED_BY_SEND_AGREEMENT: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Quote version is pinned by an open Send Agreement request and cannot be archived",
  },
  QUOTE_VERSION_OVERRIDE_NOT_ALLOWED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Quote version overrides can only be changed pre-NTP or inside an open SOW change",
  },
  QUOTE_VERSION_OVERRIDES_EMPTY: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Quote version override request supplied no fields to change",
  },
  SALES_DISCOUNT_EXCEEDS_CAP: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Sales discount exceeds the 25% cap on a non-exempt quote",
  },
  QUOTE_VERSION_STAGED_FOR_ACTIVATION: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Quote version is staged to activate on funding and cannot be archived",
  },
  REQUEST_FOR_QUOTE_HAS_STAGED_QUOTE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Manual HIC attachment refused while a staged quote version is awaiting funding",
  },
  BID_MERGED_INTO_ANOTHER_BID: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Quoting refused because the bid was merged into another bid",
  },
  QUOTE_VERSION_NOT_COPYABLE_TO_REQUEST_FOR_QUOTE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Quote version copy refused: target RFQ is not another Sunrun pre-PTO RFQ on the same bid",
  },
  QUOTE_BLOCKED_BY_REVIEW_BID_GUIDE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Quoting refused because the bid's Review Bid guide outcome does not allow a quote",
  },
};
